import React, { useEffect, useState } from 'react';
import { PlayCircle, Clock, Users, GraduationCap, Plus, Search, Award } from 'lucide-react';
import api from '../api/client';
import FormDialog from '../components/FormDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import DetailSheet from '../components/DetailSheet';
import RowActions from '../components/RowActions';
import { toast } from '../hooks/use-toast';

const FIELDS = [
  { name: 'title', label: 'Titre', required: true, full: true },
  { name: 'modules', label: 'Nombre de modules', type: 'number', required: true },
  { name: 'duration', label: 'Durée totale', placeholder: 'Ex. 4h 30', required: true },
  { name: 'format', label: 'Format', type: 'select', options: ['Vidéo + Quiz', 'SCORM', 'Interactif', 'Micro-learning', 'Vidéo + Projet'], required: true },
  { name: 'learners', label: 'Inscrits', type: 'number' },
  { name: 'completion', label: 'Taux de complétion (%)', type: 'number' },
];

export default function Elearning() {
  const [elearningCourses, setCourses] = useState([]);
  const [create, setCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [detail, setDetail] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    api.get('/courses').then(({ data }) => setCourses(data)).catch(() => {});
  }, []);

  const handleCreate = async (values) => {
    const payload = { ...values, modules: Number(values.modules), learners: Number(values.learners) || 0, completion: Number(values.completion) || 0, cover: '#4F46E5' };
    const { data } = await api.post('/courses', payload);
    setCourses((p) => [...p, data]);
    toast({ title: 'Parcours créé', description: data.title });
  };
  const handleUpdate = async (values) => {
    const { data } = await api.put(`/courses/${editing.id}`, { ...values, modules: Number(values.modules), learners: Number(values.learners), completion: Number(values.completion) });
    setCourses((p) => p.map((x) => x.id === data.id ? data : x));
    toast({ title: 'Parcours modifié' });
  };
  const handleDelete = async () => {
    await api.delete(`/courses/${deleting.id}`);
    setCourses((p) => p.filter((x) => x.id !== deleting.id));
    toast({ title: 'Parcours supprimé' });
  };

  const filtered = elearningCourses.filter((c) => c.title.toLowerCase().includes(search.toLowerCase()));
  return (
    <div className="space-y-5">
      {/* Hero */}
      <div className="rounded-2xl p-6 flex items-center gap-6" style={{ background: 'linear-gradient(120deg, #EEF2FF 0%, #E6FBF7 100%)' }}>
        <div className="w-16 h-16 rounded-2xl bg-white flex items-center justify-center shrink-0">
          <GraduationCap className="w-8 h-8 text-[#4F46E5]" />
        </div>
        <div className="flex-1">
          <h2 className="text-[22px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Votre plateforme LMS intégrée</h2>
          <p className="text-[13.5px] text-[#4A4B63] mt-1">Créez, diffusez et suivez vos parcours e-learning avec lecteur SCORM & xAPI.</p>
        </div>
        <button onClick={() => toast({ title: 'Bibliothèque SCORM', description: '14 modules standards disponibles (RGPD, Sécurité, Harcèlement…)' })} className="h-10 px-4 rounded-xl bg-white border border-[#C7D2FE] text-[13px] font-semibold text-[#4F46E5] hover:bg-[#EEF2FF] transition-colors flex items-center gap-2">
          <Award className="w-4 h-4" /> Bibliothèque SCORM
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Parcours publiés', value: 24, color: '#4F46E5' },
          { label: 'Apprenants inscrits', value: 880, color: '#14B8A6' },
          { label: 'Temps moyen / parcours', value: '4h 20', color: '#F59E0B' },
          { label: 'Taux de complétion', value: '77%', color: '#3B82F6' },
        ].map((s, i) => (
          <div key={i} className="soft-card p-4">
            <div className="text-[12px] text-[#8E8FA5] font-medium">{s.label}</div>
            <div className="text-[22px] font-bold mt-0.5" style={{ color: s.color, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8E8FA5]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un parcours…" className="w-full h-10 pl-10 pr-4 rounded-xl bg-white border border-[#EFF0F6] text-[13.5px] focus:border-[#C7D2FE] outline-none" />
        </div>
        <button onClick={() => setCreate(true)} className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center gap-2 shadow-sm ml-auto hover:opacity-95">
          <Plus className="w-4 h-4" /> Créer un parcours
        </button>
      </div>

      <FormDialog open={create} onOpenChange={setCreate} title="Nouveau parcours e-learning" onSubmit={handleCreate} fields={FIELDS} />
      <FormDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)} title="Modifier le parcours" initialValues={editing} onSubmit={handleUpdate} fields={FIELDS} />
      <ConfirmDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)} title="Supprimer ce parcours ?" description={deleting?.title} onConfirm={handleDelete} />
      <DetailSheet
        open={!!detail}
        onOpenChange={(o) => !o && setDetail(null)}
        title={detail?.title}
        subtitle={detail?.format}
        rows={detail ? [
          { label: 'Modules', value: detail.modules },
          { label: 'Durée', value: detail.duration },
          { label: 'Inscrits', value: detail.learners },
          { label: 'Complétion', value: `${detail.completion}%` },
        ] : []}
      />

      {/* Courses */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((c) => (
          <div key={c.id} onClick={() => setDetail(c)} className="soft-card overflow-hidden cursor-pointer group relative">
            <div className="absolute top-2 right-2 z-10" onClick={(e) => e.stopPropagation()}>
              <RowActions onView={() => setDetail(c)} onEdit={() => setEditing(c)} onDelete={() => setDeleting(c)} />
            </div>
            <div className="h-32 relative flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${c.cover}22 0%, ${c.cover}55 100%)` }}>
              <PlayCircle className="w-14 h-14 transition-transform group-hover:scale-110" style={{ color: c.cover }} strokeWidth={1.5} />
              <span className="absolute top-3 left-3 text-[10.5px] font-semibold px-2 py-0.5 rounded bg-white/90 text-[#1A1B2E]">{c.format}</span>
            </div>
            <div className="p-4">
              <h3 className="text-[14.5px] font-semibold text-[#1A1B2E] leading-snug" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{c.title}</h3>
              <div className="flex items-center gap-3 mt-2 text-[11.5px] text-[#6B6C85]">
                <span>{c.modules} modules</span>
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {c.duration}</span>
                <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {c.learners}</span>
              </div>
              <div className="mt-3">
                <div className="flex items-center justify-between text-[11.5px] text-[#6B6C85] mb-1">
                  <span>Complétion moyenne</span>
                  <span className="font-semibold text-[#1A1B2E]">{c.completion}%</span>
                </div>
                <div className="h-1.5 bg-[#F3F4F8] rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${c.completion}%`, background: c.cover }} />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
